import { Dialog } from "@mui/material";
import uuid from "react-uuid";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, SvgIcon, Button, Checkbox, Divider, FormControl, Grid, TextField, Chip, FormControlLabel, FormGroup } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { arrowDown, copyIcon } from "../../../../Assets/Svg/Svg";
import IconButton from "@mui/material/IconButton";
import { filterSelectTag, preprocessHTMLContent } from "./CommentLibraryHelper";
import { UpdateTemplateDataSpecific, getCommentLibrarySummaryTagList, getCommentLibraryTagList } from "../../../Helpers/ApiCall";
import { cleanPayloadOnCommentLevel, getTextColor, } from "../../../Helpers/HelperFunctions";
import { GetUTCTime } from "../../../Helpers/GetUTCTime";
import CopySummaryModal from "./CopySummaryModal";
import FroalaEditorComponent from "../../../Common/HtmlEditor/FroalaEditorComponent";
import { getDefaultCategories } from "../../../Redux/Actions/CommonActions";
import {
  getTagChipVisibility,
  mergeMuiOutlinedInputRef,
  useTagFieldCollapsedVisibleCount,
} from "./tagAutocompleteFieldUtils";

const baseFroalaConfig = {
  imagePaste: false,
  spellCheck: true,
  heightMin: 127,
  heightMax: 201,
  linkAutoPrefix: 'https://',
  linkAlwaysBlank: true,
  toolbarButtons: {
    moreText: {
      // 👇 First 7 buttons: insertLink now included
      buttons: [
        'bold', 'italic', 'underline',
        'fontFamily', 'fontSize',
        'textColor', 'insertLink' // Moved insertLink here
      ],
      align: 'left',
      buttonsVisible: 7
    },
    moreRich: {
      // 👇 Keep these out of view unless "More" is clicked
      buttons: [
        'backgroundColor',
        'alignLeft', 'alignCenter', 'alignRight',
        'formatOLSimple', 'formatUL',
        'outdent', 'indent',
        'spellChecker', 'selectAll',
        'html', 'clearFormatting'
      ],
      align: 'left',
      buttonsVisible: 0  // All hidden in "More"
    },
    moreMisc: {
      // 👇 Aligned right: undo/redo
      buttons: ['undo', 'redo'],
      align: 'right',
      buttonsVisible: 2
    }
  },
  toolbarButtonsXS: [
    ['bold', 'italic', 'underline'],
    ['fontFamily', 'fontSize'],
    ['insertLink', 'undo', 'redo']
  ],
  pluginsEnabled: [
    'wordPaste',
    'spellChecker',
    'codeBeautifier',
    'fontFamily',
    'fontSize',
    'colors',
    'align',
    'lists',
    'paragraphFormat',
    'link'
  ]
};

const getFroalaConfig = (showAI) => {
  const config = JSON.parse(JSON.stringify(baseFroalaConfig)); // deep copy to avoid mutation

  if (showAI) {
    // Inject aiHelper before undo/redo
    const miscButtons = config.toolbarButtons.moreMisc.buttons;
    if (!miscButtons.includes('aiHelper')) {
      config.toolbarButtons.moreMisc.buttons = ['aiHelper', ...miscButtons];
    }

    // Optionally increase visible count if you want to show it prominently
    config.toolbarButtons.moreMisc.buttonsVisible = Math.max(config.toolbarButtons.moreMisc.buttonsVisible, 3);
  }

  return config;
};

const EditClCommentModal = ({
  commentUuid,
  commentTitle,
  setCommentTitle,
  templateId,
  categoryId,
  lineUuid,
  showEditCLModal,
  setShowEditCLModal,
  categoryName,
  setShowDeleteModal,
  handleCloseCLEditModal,
  setCommentTag, commentDropDowns, setCommentDropDowns, summaryDropDowns, setSummaryDropDowns, commentTag, summaryTag, setSummaryTag, commentText, setCommentText, summaryText, setSummaryText
}) => {
  const editor = useRef();
  const commentEditor = useRef();
  const dispatch = useDispatch();
  const storeData = useSelector((state) => state.CommonReducer);
  const [showAIModal, setShowAIModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagsInputFocused, setTagsInputFocused] = useState(false);
  const [summaryCopyModalOpen, setSummaryCopyModalOpen] = useState(false);
  const [activeEditorId, setActiveEditorId] = useState("");
  const [tag, setTag] = useState(storeData?.clTag !== undefined ? storeData?.clTag : []);
  const [optionsOfSummaryTag, setOptionsOfSummaryTag] = useState(storeData?.clSummaryTag !== undefined ? storeData?.clSummaryTag : []);
  const rawInspectionData = storeData?.inspectionData?.data || storeData?.inspectionData;
  let prevData = rawInspectionData ? JSON.parse(JSON.stringify(rawInspectionData)) : {};
  delete prevData?.report_media_library_files;
  const templateIndex = prevData?.inspectiontemplates?.findIndex((itm) => itm?.template_id === templateId);
  const categoryIndex = prevData?.inspectiontemplates[templateIndex]?.categories?.findIndex((itm) => itm?.id === categoryId);
  const lineIndex = prevData?.inspectiontemplates[templateIndex]?.categories[categoryIndex]?.lines?.findIndex((line) => line?.uuid === lineUuid);
  const commentIndex = prevData?.inspectiontemplates[templateIndex]?.categories[categoryIndex]?.lines[lineIndex]?.comments?.findIndex((itm) => itm?.uuid === commentUuid);

  useEffect(() => {
    const templateData = prevData?.inspectiontemplates[templateIndex];
    const categoryData = prevData?.inspectiontemplates[templateIndex]?.categories[categoryIndex];
    dispatch(getDefaultCategories(`?category_uuid=${categoryData?.uuid}&template_guid=${templateData?.template_guid}&is_narrative_template=${templateData?.is_narrative_template}`));
  }, []);

  useEffect(() => {
    if (showEditCLModal) {
      getCommentLibraryTagList(dispatch);
      getCommentLibrarySummaryTagList(dispatch);
    }
  }, [showEditCLModal]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleTagOpen = () => {
    setTagOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTagClose = () => {
    setTagOpen(false);
  };

  const handleChangeComment = (id, content) => {
    setCommentText(prevState => ({ ...prevState, innerHTML: content }));
  };

  const handleChangeSumary = (id, content) => {
    setSummaryText(prevState => ({ ...prevState, innerHTML: content }));
    setCopied(false);
  };

  const handleCopyButton = () => {
    const commentEditorInstance = commentEditor?.current?.editor;
    const currentCommentContent = commentEditorInstance?.html?.get() || commentText?.innerHTML;
    if (currentCommentContent?.length === 0 || currentCommentContent === "<p><br></p>" || currentCommentContent === "<p></p>") {
      return null;
    } else if (summaryText?.innerHTML?.length === 0 || summaryText?.innerHTML === "<p><br></p>") {
      handleCopyComment();
    } else {
      setSummaryCopyModalOpen(true);
    }
  };

  async function handleCopyComment() {
    const commentEditorInstance = commentEditor?.current?.editor;
    const currentCommentContent = commentEditorInstance?.html?.get() || commentText?.innerHTML;
    if (!currentCommentContent || currentCommentContent === "<p><br></p>" || currentCommentContent === "<p></p>") {
      return;
    }
    if (currentCommentContent !== commentText?.innerHTML) {
      setCommentText(prevState => ({ ...prevState, innerHTML: currentCommentContent }));
    }
    let newCommentText = currentCommentContent;
    let updatedText = newCommentText;
    const editorInstance = editor?.current?.editor;
    const uuidMapping = {};
    const newCommentDropdownList = commentText?.dropdownList?.map((dropdown) => {
      const uniqueId = uuid();
      uuidMapping[dropdown?.dropdown_uuid] = uniqueId;
      return {
        ...dropdown,
        dropdown_uuid: uniqueId,
        name: dropdown?.name || "",
        selected_value: dropdown?.selected_value,
      };
    });
    Object.keys(uuidMapping).forEach((oldId) => {
      const newId = uuidMapping[oldId];
      const regex = new RegExp(oldId, "g");
      updatedText = updatedText.replace(regex, newId);
    });
    newCommentText = updatedText;
    let commentDataEditor = newCommentText.replace(/class="comment-dropdown"/g, 'class="summary-dropdown"');
    setSummaryText({ innerHTML: commentDataEditor, dropdownList: newCommentDropdownList });
    editorInstance?.html?.set(commentDataEditor);
    setSummaryDropDowns(newCommentDropdownList);
    setSummaryCopyModalOpen(false);
    setCopied(true);
  }

  const handleEditClComment = async () => {
    let copiedConvertedSummaryText = filterSelectTag(summaryText?.innerHTML);
    let convertedCommentText = filterSelectTag(commentText?.innerHTML);
    let summaryConvertedText = filterSelectTag(summaryText?.innerHTML);
    if (commentIndex !== -1) {
      let comment = prevData.inspectiontemplates[templateIndex].categories[categoryIndex].lines[lineIndex].comments[commentIndex];
      Object.assign(comment, {
        comment_dropdowns: commentText?.dropdownList?.length ? commentText?.dropdownList : commentDropDowns || [],
        comment_text: convertedCommentText.includes("undefined") ? "" : convertedCommentText || "",
        title: commentTitle,
        tags: commentTag,
        summaries: summaryTag,
        summary_comment_text: copied ? (copiedConvertedSummaryText.includes("undefined") ? "" : copiedConvertedSummaryText) : summaryConvertedText.includes("undefined") ? "" : summaryConvertedText,
        summary_comment_dropdowns: summaryText?.dropdownList?.length ? summaryText?.dropdownList : summaryDropDowns || [],
        updated_at: await GetUTCTime(),
        is_selected_updated_at: await GetUTCTime(),
      });
      let filteredObject = await cleanPayloadOnCommentLevel(prevData, templateIndex, categoryIndex, lineIndex, commentIndex, 'editComment');
      await UpdateTemplateDataSpecific(dispatch, prevData, filteredObject);
    }
    setShowEditCLModal(false);
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    setTag(storeData?.clTag !== undefined ? storeData?.clTag : []);
    setOptionsOfSummaryTag(storeData?.clSummaryTag !== undefined ? storeData?.clSummaryTag : []);
  }, [storeData?.clTag, storeData?.clSummaryTag]);

  const handleDropdownList = (name, value) => {
    if (name === "commentText") {
      setCommentDropDowns(value);
    } else if (name === "summaryText") {
      setSummaryDropDowns(value);
    }
  }

  const commentEditorData = useMemo(() => ({
    comment_text: commentText?.innerHTML,
    comment_dropdowns: commentDropDowns,
    comment_uuid: commentUuid
  }), [commentText?.innerHTML, commentDropDowns, commentUuid]);

  const summaryEditorData = useMemo(() => ({
    summary_comment_text: summaryText?.innerHTML,
    summary_comment_dropdowns: summaryDropDowns,
    comment_uuid: commentUuid
  }), [summaryText?.innerHTML, summaryDropDowns, commentUuid]);

  const tagAutocompleteValue = useMemo(
    () =>
      (tag || []).filter((tagItem) =>
        (commentTag || []).some((selectedTag) => String(selectedTag?.id) === String(tagItem?.id))
      ),
    [tag, commentTag]
  );

  const { tagsInputRootRef, collapsedVisibleTagCount } = useTagFieldCollapsedVisibleCount({
    tagOpen,
    tagsInputFocused,
    selectedTags: tagAutocompleteValue,
  });

  return (
    <>
      <Dialog
        className="dialog-container-comment-library edit_cl_comment_container"
        open={showEditCLModal}
        onClose={handleCloseCLEditModal}
        aria-labelledby="category-modal"
        aria-describedby="category-modal-description"
        disableEnforceFocus={false}
        disablePortal
      >
        <div className="close-wrapper-new-category-right">
          <CloseIcon onClick={handleCloseCLEditModal} />
        </div>
        <DialogTitle className="edit_cl_dialog_title">{categoryName} - Edit comment</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid item xs={12}>
            <FormControl variant="standard" className="forms-control">
              <label className="cl-new-comment-input-form-label" htmlFor="title">
                TITLE
              </label>
              <TextField
                id="cl_title"
                className="input-textfield"
                size="small"
                variant="outlined"
                value={commentTitle}
                onChange={(e) => setCommentTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                type="text"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ccc",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ccc",
                    },
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid container>
            <Grid item xs={12} sm={5.75} className="cl-add-comment-sections">
              <Grid container className="input-controll">
                <Grid item xs={12} sm={12}>
                  <FormControl variant="standard" className="forms-control">
                    <label className="cl-new-comment-input-form-label" htmlFor="title">
                      TAGS
                    </label>
                    <Autocomplete
                      className="forms-control-tag custom-color autocompleteCustom"
                      id="tags-outlined"
                      multiple
                      size="small"
                      options={tag}
                      value={tagAutocompleteValue}
                      onClick={!tagOpen ? handleTagOpen : handleTagClose}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option?.name}
                      isOptionEqualToValue={(option, value) => option?.id != null && value?.id != null && String(option.id) === String(value.id)}
                      open={tagOpen}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ccc",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ccc",
                          },
                        },
                      }}
                      onChange={(e, value, reason) => {
                        const selectedTag = value[value?.length - 1];
                        if (reason === "removeOption") {
                          setCommentTag(value);
                          return;
                        }
                        const isTagSelected = commentTag?.some((tag) => tag?.id === selectedTag?.id);
                        if (isTagSelected) {
                          setCommentTag((prevTags) => prevTags?.filter((tag) => tag?.id !== selectedTag?.id));
                        } else {
                          setCommentTag((prevTags) => [...prevTags, selectedTag]);
                        }
                      }}
                      renderTags={(value, getTagProps) => {
                        const { visible, hiddenCount } = getTagChipVisibility(value, {
                          tagOpen,
                          tagsInputFocused,
                          collapsedVisibleTagCount,
                        });
                        return (
                          <>
                            {visible.map((option, index) => (
                              <Chip
                                variant="outlined"
                                size="small"
                                deleteIcon={<CloseIcon sx={{ color: `${getTextColor(option?.color)} !important`, fontSize: 16, backgroundColor: "transparent !important" }} />}
                                label={option?.name}
                                {...getTagProps({ index })}
                                sx={{
                                  borderRadius: "35px",
                                  border: "1px solid #CED2DA !important",
                                  backgroundColor: `${option?.color} !important`,
                                  color: `${getTextColor(option?.color)} !important`,
                                  maxWidth: 120,
                                  paddingLeft: "10px",
                                }}
                              />
                            ))}
                            {hiddenCount > 0 && (
                              <Box component="span" sx={{ ml: 0.5, fontSize: "14px", lineHeight: "24px", color: "#333333" }}>
                                {`+${hiddenCount}`}
                              </Box>
                            )}
                          </>
                        );
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                          <Box
                            sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                            style={{
                              marginRight: 8,
                            }}
                          />
                          {option?.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={commentTag?.length === 0 ? "Add tags" : ""}
                          onClick={!tagOpen ? handleTagOpen : handleTagClose}
                          onFocus={(e) => {
                            params.inputProps?.onFocus?.(e);
                            setTagsInputFocused(true);
                          }}
                          onBlur={(e) => {
                            params.inputProps?.onBlur?.(e);
                            setTagsInputFocused(false);
                            handleTagClose();
                          }}
                          InputProps={{
                            ...params.InputProps,
                            ref: mergeMuiOutlinedInputRef(tagsInputRootRef, params.InputProps.ref),
                            style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: "20px" },
                            endAdornment: (
                              <>
                                <SearchIcon className="customSearch" style={{ color: "#9b9b9b", marginRight: "8px" }} />
                                <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={!tagOpen ? handleTagOpen : handleTagClose}>
                                  {arrowDown}
                                </SvgIcon>
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl variant="standard" className="forms-control">
                    <label id="commentText" className="cl-new-comment-input-form-label" htmlFor="comment" style={{ marginBottom: "9px" }}>
                      COMMENT
                    </label>
                    <FroalaEditorComponent templateId={templateId} setActiveEditorId={setActiveEditorId}
                      onFocusEditor={() => { setActiveEditorId("commentText"); }} type={"edit"} setCopied={setCopied} isEdit={true}
                      categoryName={categoryName} categoryNameFromLocation={""} showAIModal={showAIModal} categoryId={categoryId}
                      setShowAIModal={setShowAIModal} commentTitle={commentTitle} setCommentText={setCommentText}
                      showAI={storeData?.inspectionData?.data?.is_comment_helper_enable === 1 ? true : false}
                      editorData={commentText} commentEditorRef={commentEditor} editor={commentEditor} id={"commentText"}
                      onChange={handleChangeComment} froalaConfig={getFroalaConfig(storeData?.inspectionData?.data?.is_comment_helper_enable === 1)}
                      handleDropdownList={handleDropdownList} commentData={commentEditorData} filterTagList={tag} />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={0.5} display={"flex"} alignItems={"center"} justifyContent={"center"} pl={0.2} pt={14}>
              <IconButton onClick={handleCopyButton} className="summary-section-copy-icon" sx={{ "&:hover": { backgroundColor: "transparent" } }}>
                <SvgIcon viewBox="">{copyIcon}</SvgIcon>
              </IconButton>
            </Grid>
            <Grid item xs={12} sm={5.75} className="cl-add-comment-sections">
              <Grid container className="input-controll">
                <Grid item xs={12} sm={12}>
                  <FormControl variant="standard" className="forms-control">
                    <label className="cl-new-comment-input-form-label" htmlFor="title">
                      SUMMARY SECTION
                    </label>
                    <Autocomplete
                      className="forms-control-tag custom-color autocompleteSummaryCustom"
                      id="tags-outlined"
                      multiple
                      size="small"
                      limitTags={2}
                      options={optionsOfSummaryTag}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option?.name}
                      openOnFocus
                      open={open}
                      value={summaryTag}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ccc",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ccc",
                          },
                        },
                      }}
                      onChange={(e, value, reason) => {
                        const selectedTag = value[value?.length - 1];
                        // If the `reason` is "removeOption", directly update the state
                        if (reason === "removeOption") {
                          setSummaryTag(value);
                          return;
                        }
                        const isTagSelected = summaryTag?.some((tag) => tag?.id === selectedTag?.id);
                        if (isTagSelected) {
                          // If the tag is already selected, remove it from the state
                          setSummaryTag((prevTags) => prevTags?.filter((tag) => tag?.id !== selectedTag?.id));
                        } else {
                          // If the tag is not selected, add it to the state
                          setSummaryTag((prevTags) => [...prevTags, selectedTag]);
                        }
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            className="custom-chip"
                            variant="outlined"
                            deleteIcon={<CloseIcon />}
                            label={
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Box
                                  sx={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: option?.color,
                                    borderRadius: "50%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginRight: "8px",
                                  }}
                                />
                                <div style={{ fontSize: "14px" }}>{option?.name}</div>
                              </Box>
                            }
                            {...getTagProps({ index })}
                            sx={{
                              padding: "2px 2px 2px 10px !important",
                              fontSize: "10px !important",
                            }}
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox style={{ borderRadius: "50%" }} icon={icon} checkedIcon={checkedIcon} checked={summaryTag.some((tag) => tag.name === option.name)} />
                          <Box
                            sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                            style={{
                              marginRight: 8,
                            }}
                          />
                          {option?.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={summaryTag?.length === 0 ? "No summary selected" : ""}
                          onClick={!open ? handleOpen : handleClose}
                          onBlur={handleClose}
                          InputProps={{
                            ...params.InputProps,
                            style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400 },
                            endAdornment: (
                              <>
                                <SvgIcon className={`customArrowDown ${open ? "arrowActive" : ""}`} viewBox="" onClick={!open ? handleOpen : handleClose}>
                                  {arrowDown}
                                </SvgIcon>
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl variant="standard" className="forms-control">
                    <label id="summaryText" className="cl-new-comment-input-form-label" htmlFor="summary-comment" style={{ marginBottom: "9px" }}>
                      SUMMARY COMMENT
                    </label>
                    {!showAIModal ? <FroalaEditorComponent templateId={templateId} setActiveEditorId={setActiveEditorId}
                      onFocusEditor={() => { setActiveEditorId("summaryText"); }} setCommentSummary={setSummaryText} type={"edit"} setCopied={setCopied} isEdit={true}
                      categoryName={categoryName} categoryNameFromLocation={""} showAIModal={showAIModal} setShowAIModal={setShowAIModal}
                      showAI={false} editorData={summaryText} summaryEditorRef={editor} editor={editor}
                      id={"summaryText"} onChange={handleChangeSumary} froalaConfig={getFroalaConfig(false)}
                      copy={copied} handleDropdownList={handleDropdownList} commentData={summaryEditorData}
                      filterTagList={tag} categoryId={categoryId} /> : ""}
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions className="comment-library-action" style={{ height: "64px" }}>
          <div className={`comment-library-checkbox-container hidden`}>
            <FormGroup>
              <FormControlLabel className="cl_checkbox" control={<Checkbox />} label="Push to template" />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                className="cl_checkbox"
                control={<Checkbox sx={{ "&.Mui-checked": { color: "#4282E2" } }} />}
                label="Push to library"
              />
            </FormGroup>
          </div>
          <div className="comment-library-action-btn">
            <div className="cl-modal-delete-comment" onClick={() => setShowDeleteModal(true)} style={{ cursor: "pointer" }}>
              delete comment
            </div>
            <Button
              disabled={((commentTitle?.trim() === "" || !commentTitle) && (commentText?.innerHTML?.trim() === "" || commentText?.innerHTML?.trim() === "<p><br></p>" || preprocessHTMLContent(commentText?.innerHTML) === "" || !commentText?.innerHTML)) ? true : false}
              className="cl-edit-comment-btn"
              onClick={((commentTitle?.trim() === "" || !commentTitle) && (commentText?.innerHTML?.trim() === "" || commentText?.innerHTML?.trim() === "<p><br></p>" || preprocessHTMLContent(commentText?.innerHTML) === "" || !commentText?.innerHTML)) ? undefined : handleEditClComment}
            >
              Save comment
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      {summaryCopyModalOpen && <CopySummaryModal
        open={summaryCopyModalOpen}
        handleClose={setSummaryCopyModalOpen}
        handleSubmit={handleCopyComment}
        submitButtonTitle={"REPLACE"}
        dialogTitle={"Replace summary comment ?"}
        dialogContentText={"Are you sure you want to replace the summary comment ?"}
      />}
    </>
  );
};

export default EditClCommentModal;